// ─── Paychangu Service ────────────────────────────────────────────────────────
const PAYCHANGU_API   = "https://api.paychangu.com";
const API_KEY         = process.env.REACT_APP_PAYCHANGU_KEY || "sec-live-cd9dIsz7s22ob7ArhgdC2uwgcaw4ts6Q";
const OPERATOR_REF_IDS = {
  airtel: "20be6c20-adeb-4b5b-a7ba-0769820df4fb",
  mpamba: "27494cb5-ba9e-437f-a114-4e7a7686bcca",
};

export const MOBILE_OPERATORS = [
  { id: "airtel", label: "Airtel Money" },
  { id: "mpamba", label: "TNM Mpamba"  },
];

export const PAYMENT_METHODS = [
  { id: "mobile_money",  label: "Mobile Money",   sub: "Airtel / TNM Mpamba" },
  { id: "bank_transfer", label: "Bank Transfer",   sub: "Instant bank transfer" },
];

const normalizePhone = (phone) => {
  const clean = phone.replace(/\s+/g, "");
  if (clean.startsWith("+265")) return "0" + clean.slice(4);
  if (clean.startsWith("265"))  return "0" + clean.slice(3);
  return clean;
};

export const generateChargeId = () => {
  const ts  = Date.now().toString(36).toUpperCase();
  const rnd = Math.random().toString(36).substring(2, 7).toUpperCase();
  return `GVS-${ts}-${rnd}`;
};

// ── Mobile Money: initialize ──────────────────────────────────────────────────
export const initializeMobilePayment = async ({ firstName, lastName, email, phone, amount, chargeId, operator }) => {
  try {
    const operatorRefId = OPERATOR_REF_IDS[operator] || OPERATOR_REF_IDS.airtel;

    const response = await fetch(`${PAYCHANGU_API}/mobile-money/payments/initialize`, {
      method: "POST",
      headers: {
        Authorization:  `Bearer ${API_KEY}`,
        accept:         "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        mobile_money_operator_ref_id: operatorRefId,
        mobile:     normalizePhone(phone),
        email,
        amount:     String(amount),
        first_name: firstName,
        last_name:  lastName,
        charge_id:  chargeId,
      }),
    });

    const data = await response.json();
    console.log("Mobile money init response:", data);

    if (!response.ok || data.status === "error") {
      const msg = data.message;
      const error = typeof msg === "string"
        ? msg
        : msg && typeof msg === "object"
          ? Object.values(msg).flat().join(" ")
          : "Payment initialization failed.";
      return { success: false, error };
    }

    return { success: true, data };
  } catch (err) {
    console.error("initializeMobilePayment error:", err);
    return { success: false, error: "Network error. Please try again." };
  }
};

// ── Mobile Money: verify ──────────────────────────────────────────────────────
export const verifyMobilePayment = async (chargeId) => {
  try {
    const response = await fetch(`${PAYCHANGU_API}/mobile-money/payments/${chargeId}/verify`, {
      headers: {
        Authorization: `Bearer ${API_KEY}`,
        accept:        "application/json",
      },
    });

    const data = await response.json();
    console.log("Mobile money verify response:", JSON.stringify(data, null, 2));

    const rawStatus = data?.data?.status || data?.status || "pending";
    return { success: true, status: rawStatus, raw: data };
  } catch (err) {
    console.error("verifyMobilePayment error:", err);
    return { success: false, status: "pending" };
  }
};

// ── Bank Transfer: initialize ─────────────────────────────────────────────────
// Returns bank account details the customer must transfer to
export const initializeBankTransfer = async ({ amount, chargeId }) => {
  try {
    const response = await fetch(`${PAYCHANGU_API}/direct-charge/payments/initialize`, {
      method: "POST",
      headers: {
        Authorization:  `Bearer ${API_KEY}`,
        accept:         "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        payment_method: "mobile_bank_transfer",
        amount:         String(amount),
        currency:       "MWK",
        charge_id:      chargeId,
      }),
    });

    const data = await response.json();
    console.log("Bank transfer init response:", JSON.stringify(data, null, 2));

    if (!response.ok || data.status === "error") {
      const msg = data.message;
      const error = typeof msg === "string"
        ? msg
        : msg && typeof msg === "object"
          ? Object.values(msg).flat().join(" ")
          : "Bank transfer initialization failed.";
      return { success: false, error };
    }

    // Extract the bank account details the customer transfers to
    const account = data?.data?.payment_account_details;
    return {
      success: true,
      bankName:      account?.bank_name      || "",
      accountNumber: account?.account_number || "",
      accountName:   account?.account_name   || "",
      expiresAt:     account?.account_expiration_timestamp || null,
      raw:           data,
    };
  } catch (err) {
    console.error("initializeBankTransfer error:", err);
    return { success: false, error: "Network error. Please try again." };
  }
};

// ── Bank Transfer: verify ─────────────────────────────────────────────────────
export const verifyBankTransfer = async (chargeId) => {
  try {
    const response = await fetch(`${PAYCHANGU_API}/direct-charge/transactions/${chargeId}/details`, {
      headers: {
        Authorization: `Bearer ${API_KEY}`,
        accept:        "application/json",
      },
    });

    const data = await response.json();
    console.log("Bank transfer verify response:", JSON.stringify(data, null, 2));

    const rawStatus = data?.data?.transaction?.status || data?.status || "pending";
    return { success: true, status: rawStatus, raw: data };
  } catch (err) {
    console.error("verifyBankTransfer error:", err);
    return { success: false, status: "pending" };
  }
};

// ── Generic poller — works for both mobile money and bank transfer ─────────────
// verifyFn: the verify function to call (verifyMobilePayment or verifyBankTransfer)
export const pollPaymentConfirmation = (chargeId, verifyFn, {
  intervalMs = 5000,    // check every 5 seconds
  timeoutMs  = 600000,  // 10 min timeout for bank transfer (customer needs time to transfer)
} = {}) => {
  return new Promise((resolve) => {
    const start = Date.now();

    const check = async () => {
      if (Date.now() - start >= timeoutMs) {
        resolve({ confirmed: false, reason: "timeout" });
        return;
      }

      const result = await verifyFn(chargeId);
      const status = (result.status || "").toLowerCase();
      console.log("Polling status:", status);

      if (status === "success" || status === "successful" || status === "completed") {
        resolve({ confirmed: true });
        return;
      }

      if (status === "failed" || status === "cancelled" || status === "canceled" || status === "error") {
        resolve({ confirmed: false, reason: status });
        return;
      }

      setTimeout(check, intervalMs);
    };

    setTimeout(check, 3000);
  });
};

// ── Mobile Money: payout (withdrawal) ───────────────────────────────────────────
// Calls PayChangu directly from the browser, same trust model as the payment
// functions above — a hardened version (server-side, admin-verified) exists
// in netlify/functions/withdrawal-payout.js if you want to switch back later.
export const requestPayout = async ({ operator, phone, amount, chargeId }) => {
  try {
    const operatorRefId = OPERATOR_REF_IDS[operator] || OPERATOR_REF_IDS.airtel;

    const response = await fetch(`${PAYCHANGU_API}/mobile-money/payouts/initialize`, {
      method: "POST",
      headers: {
        Authorization:  `Bearer ${API_KEY}`,
        accept:         "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        mobile_money_operator_ref_id: operatorRefId,
        mobile:     normalizePhone(phone),
        amount:     String(amount),
        charge_id:  chargeId,
      }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data || data.status === "error") {
      const msg = data?.message;
      const error = typeof msg === "string"
        ? msg
        : msg && typeof msg === "object"
          ? Object.values(msg).flat().join(" ")
          : `Payout failed (${response.status}).`;
      return { success: false, error };
    }

    return { success: true, data: data.data };
  } catch (err) {
    console.error("requestPayout error:", err);
    return { success: false, error: "Network error. Please try again." };
  }
};

// Keep old name for backwards compat with existing Checkout.js imports
export const initializePayment       = initializeMobilePayment;
export const verifyPayment           = verifyMobilePayment;
